/**
 * useSystemHealth — data hooks for the System Health page.
 * Polling reads the engine's cached snapshot (re-checked only when older than its TTL), so an open
 * page never multiplies load on the services.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { systemService } from '@/services/ai/systemService';
import { queryKeys } from '@/lib/queryClient';

const KEYS = queryKeys.system;
const POLL_MS = 60000;

export function useSystemHealth() {
  return useQuery({ queryKey: KEYS.health, queryFn: systemService.getHealth, refetchInterval: POLL_MS, retry: 1 });
}

/**
 * Whether the AI engine (GPU pod) is answering. Screens that read through the engine gate on it.
 * Checked ONCE each time such a screen opens or the page is refreshed — no background polling (owner
 * decision 2026-10-13): the notice tells the user to refresh to check again.
 */
export function useEngineReachable() {
  return useQuery({
    queryKey: KEYS.engineReachable,
    queryFn: systemService.isEngineReachable,
    retry: false,
    // An answer cached from an earlier visit (pod still booting) must never count.
    refetchOnMount: 'always',
  });
}

/**
 * The engine's state FOR THIS VISIT: `checking` until an answer arrives that was fetched after this
 * screen opened (so a stale cached answer never counts), then `up` or `down`.
 */
export function useEngineStatus() {
  const engine = useEngineReachable();
  const [openedAt] = useState(() => Date.now());
  const checked = engine.dataUpdatedAt >= openedAt;
  return { checking: !checked, up: checked && engine.data === true, down: checked && engine.data === false };
}

export function useSystemHealthRefresh() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: systemService.refresh,
    onSuccess: (data) => qc.setQueryData(KEYS.health, data),
  });
}
